import React, { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { FaArrowLeft, FaSpinner } from "react-icons/fa";
import BackButton from "../components/BackButton";

const backendBase = `http://${window.location.hostname === 'localhost' ? 'localhost' : '192.168.1.159'}:3001`;

export default function Story() {
  const location = useLocation();
  const navigate = useNavigate();
  const { scene, imageUrl, title, technique, inspiration } = location.state || {};

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentScene, setCurrentScene] = useState(scene);
  const [showCover, setShowCover] = useState(true);

  const fetchStoryScene = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      console.log("🔍 DEBUG: Starting story scene generation");
      console.log("🔍 DEBUG: Request payload:", {
        imageUrl: imageUrl?.substring(0, 100) + "...",
        title,
        technique,
        inspiration,
        artistName: 'Young Artist',
        sceneNumber: 1,
        prompt: 'Create an engaging story scene based on this artwork'
      });

      const requestUrl = `${backendBase}/api/story/generate-scene`;
      console.log("🔍 DEBUG: Making request to:", requestUrl);

      const response = await fetch(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          imageUrl,
          title,
          technique,
          inspiration,
          artistName: 'Young Artist',
          sceneNumber: 1,
          prompt: 'Create an engaging story scene based on this artwork'
        }),
      });

      console.log("🔍 DEBUG: Response status:", response.status);
      console.log("🔍 DEBUG: Response headers:", Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        const errorData = await response.json();
        console.error("❌ Story API error:", errorData);
        throw new Error(errorData.error || 'Failed to generate story scene');
      }

      const responseText = await response.text();
      console.log("🔍 DEBUG: Raw response text:", responseText);

      let data;
      try {
        data = JSON.parse(responseText);
        console.log("🔍 DEBUG: Parsed JSON data:", data);
      } catch (parseError) {
        console.error("❌ JSON parse error:", parseError);
        throw new Error('Invalid JSON response from server');
      }

      if (!data.scene) {
        console.error("❌ Missing scene data in response:", data);
        throw new Error('Invalid response format: missing scene data');
      }

      console.log("✅ Successfully received scene data:", data.scene);
      setCurrentScene(data.scene);
    } catch (error) {
      console.error("❌ Story error:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  }, [imageUrl, title, technique, inspiration]);

  useEffect(() => {
    if (!scene && imageUrl && title) {
      fetchStoryScene();
    }
  }, [scene, imageUrl, title, fetchStoryScene]);

  const handleBack = () => {
    navigate(-1);
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-amber-100 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-xl shadow-lg max-w-md">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-amber-100 flex justify-center px-4 py-8 md:py-10">
      <div className="relative z-10 w-full max-w-4xl">
        <div className="bg-white/95 rounded-[32px] shadow-2xl border-2 border-amber-300 p-4 md:p-6">
      {loading && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-amber-800 text-sm flex items-center gap-2">
          <FaSpinner className="animate-spin" />
          <span>Creating your magical story...</span>
        </div>
      )}
      <BackButton className="mb-6" />
      <div className="max-w-4xl mx-auto">
        <button
          onClick={handleBack}
          className="flex items-center text-amber-700 hover:text-amber-800 mb-6"
        >
          <FaArrowLeft className="mr-2" />
          Back
        </button>

        <AnimatePresence mode="wait">
          {showCover ? (
            <motion.div
              key="cover"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-xl shadow-lg p-8 mb-8"
            >
              <div className="text-center">
                <h1 className="text-3xl font-bold text-amber-800 mb-4">{title}</h1>
                <div className="relative w-64 h-80 mx-auto mb-6">
                  <img
                    src={imageUrl}
                    alt={title}
                    className="w-full h-full object-cover rounded-lg shadow-md"
                  />
                </div>
                <button
                  onClick={() => setShowCover(false)}
                  className="px-6 py-3 bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors"
                >
                  Start Reading
                </button>
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="story"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="bg-white rounded-xl shadow-lg p-8"
            >
              {currentScene && (
                <>
                  <h2 className="text-2xl font-bold text-amber-800 mb-4">
                    {currentScene.title}
                  </h2>
                  <div className="prose max-w-none">
                    <p className="text-gray-700 mb-6">{currentScene.description}</p>
                    
                    <div className="mb-6">
                      <h3 className="text-xl font-semibold text-amber-700 mb-2">Characters</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {currentScene.characters.map((character, index) => (
                          <div key={index} className="bg-amber-50 p-4 rounded-lg">
                            <h4 className="font-medium text-amber-800">{character.name}</h4>
                            <p className="text-gray-600">{character.description}</p>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="mb-6">
                      <h3 className="text-xl font-semibold text-amber-700 mb-2">Setting</h3>
                      <p className="text-gray-700">{currentScene.setting}</p>
                    </div>

                    <div className="mb-6">
                      <h3 className="text-xl font-semibold text-amber-700 mb-2">Mood</h3>
                      <p className="text-gray-700">{currentScene.mood}</p>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
        </div>
      </div>
    </div>
  );
} 